import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Send, AlertCircle, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScreenshotPaste } from "./screenshot-paste";
import { EventLogUpload } from "./event-log-upload";
import { IntentReview } from "./intent-review";
import { BugBarPanel, belowBugBarFrom, bugBarBlocks } from "./bug-bar-panel";
import type { FogbugzBugBarResult } from "@ttc-spectree/toolbox-contract/fogbugz-formulator";
import type { DedupeCase, AuditScore, InterviewCoverage } from "@/hooks/queries/use-fogbugz";
import {
  useDedupe,
  useIntentDocument,
  useReviewIntent,
  useSubmitInterview,
} from "@/hooks/queries/use-fogbugz";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/api/client";
import { safeHttpUrl } from "@/lib/safe-url";
import { cn } from "@/lib/utils";
import { slotLabel } from "@/lib/intent-labels";

interface Message {
  role: "user" | "assistant";
  content: string;
}

/**
 * Wizard phases.
 *
 * `review` sits between `interview` and `dedupe`: it is the playbook's
 * originator-corrects-the-draft step, where the reporter edits the assembled
 * `intent.md` before anything is filed (Resolved Decision 1 in
 * `docs/analysis/fogbugz-intent-interview.md`).
 */
type Phase = "interview" | "review" | "dedupe" | "submit";

interface InterviewWizardProps {
  sessionId: string;
  firstQuestion: string;
}

export function InterviewWizard({ sessionId, firstQuestion }: InterviewWizardProps) {
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: firstQuestion },
  ]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [phase, setPhase] = useState<Phase>("interview");
  const [error, setError] = useState<string | null>(null);
  // Slot coverage as last reported by the server. Completion is a server
  // decision (`computeCoverage`), never a model claim or a client guess, so
  // every phase transition out of `interview` reads this rather than a bare
  // boolean flag.
  const [coverage, setCoverage] = useState<InterviewCoverage | null>(null);
  // The Bug bar from the latest review save or a BELOW_BUG_BAR submit refusal
  // (epic PI-SPEC-SW-E309). Wins over the intent query's copy, which may lag a
  // save; null until either has happened.
  const [checkedBugBar, setCheckedBugBar] = useState<FogbugzBugBarResult | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  // Panel height needed to show the last two messages in full; null until measured.
  const [minPanelHeight, setMinPanelHeight] = useState<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const navigate = useNavigate();

  const intentQuery = useIntentDocument(phase === "review" ? sessionId : null);
  const reviewMutation = useReviewIntent();
  const dedupeQuery = useDedupe(phase === "dedupe" ? sessionId : null);
  const submitMutation = useSubmitInterview();
  const bugBar = checkedBugBar ?? intentQuery.data?.data.bugBar ?? null;

  // Auto-scroll the message list to the bottom when messages change. Only the list
  // scrolls: scrolling the page here would push the conversation out of view once the
  // panel has grown past the viewport.
  useEffect(() => {
    const area = messagesAreaRef.current;
    if (area) area.scrollTop = area.scrollHeight;
  }, [messages]);

  // The last two messages (the newest question and the answer before it) must always be
  // fully visible. The panel's base height leaves the message list whatever the progress
  // bar, uploaders, and input don't use, so when those two messages are taller than that,
  // grow the panel to fit them and let the page scroll instead of squeezing the list.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const area = messagesAreaRef.current;
    if (phase !== "interview" || !panel || !area) return;

    const lastTwoRows = () =>
      Array.from(area.querySelectorAll<HTMLElement>("[data-message-row]")).slice(-2);

    const measure = () => {
      const rows = lastTwoRows();
      const first = rows[0];
      const last = rows[rows.length - 1];
      if (!first || !last) {
        setMinPanelHeight(null);
        return;
      }
      const style = window.getComputedStyle(area);
      const areaChrome =
        (parseFloat(style.paddingTop) || 0) +
        (parseFloat(style.paddingBottom) || 0) +
        (parseFloat(style.borderTopWidth) || 0) +
        (parseFloat(style.borderBottomWidth) || 0);
      const lastTwoHeight = last.offsetTop + last.offsetHeight - first.offsetTop;
      const everythingElse = panel.offsetHeight - area.offsetHeight;
      const needed = Math.ceil(lastTwoHeight + areaChrome + everythingElse);
      setMinPanelHeight(needed > 0 ? needed : null);
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;
    // Re-measure while a reply streams in and when the uploaders above the input grow.
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    observer.observe(area);
    for (const row of lastTwoRows()) observer.observe(row);
    return () => {
      observer.disconnect();
    };
  }, [messages, phase]);

  // Abort any in-flight stream when the component unmounts to avoid
  // setState-on-unmounted warnings and a leaked fetch/reader.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  const handleSend = async (messageText?: string, options?: { appendUserMessage?: boolean }) => {
    const userMessage = messageText || input.trim();
    if (!userMessage || isStreaming) return;

    const appendUserMessage = options?.appendUserMessage ?? true;

    setInput("");
    setError(null);

    // Add user message (skipped on retry — the previous attempt already
    // recorded the user's message; only the assistant placeholder needs to
    // be re-created).
    if (appendUserMessage) {
      const newUserMessage: Message = { role: "user", content: userMessage };
      setMessages((prev) => [...prev, newUserMessage]);
    }

    // Create placeholder for streaming assistant response. The index is
    // computed from the current messages length plus 1 when we just appended
    // a user message, otherwise just the current length (the placeholder
    // sits immediately after the existing trailing user message).
    const assistantMessageIndex = appendUserMessage ? messages.length + 1 : messages.length;
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    setIsStreaming(true);

    try {
      // Create abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Use ApiClient.stream() so the request participates in auth refresh
      // and 401-retry logic — raw `fetch()` with a stale accessToken from the
      // store would bypass the silent refresh that ApiClient.request() does.
      // EventSource can't be used here because we need a POST body.
      const response = await api.stream(`/fogbugz/interview/${sessionId}/reply`, {
        method: "POST",
        body: JSON.stringify({ message: userMessage }),
        signal: abortController.signal,
      });

      if (!response.body) {
        throw new Error("Response body is null");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulatedContent = "";
      let currentEventType = "message";
      let sseError: { code?: string; message?: string } | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          // Blank line terminates an SSE event; reset event type for the next one.
          if (!line.trim()) {
            currentEventType = "message";
            continue;
          }

          // Parse SSE format: "event: <type>\ndata: {...}\n\n"
          if (line.startsWith("event:")) {
            currentEventType = line.slice(6).trim();
            continue;
          }

          if (line.startsWith("data:")) {
            const dataStr = line.slice(5).trim();

            // Handle server-emitted error events (e.g., post-hijack failures).
            // The server uses `event: error` with a `{ code, message }` payload.
            if (currentEventType === "error") {
              try {
                sseError = JSON.parse(dataStr) as { code?: string; message?: string };
              } catch (e) {
                console.error("Failed to parse SSE error data:", e);
                sseError = { message: "Stream error" };
              }
              continue;
            }

            try {
              const data = JSON.parse(dataStr) as {
                delta?: string;
                response?: string;
                isComplete?: boolean;
                coverage?: InterviewCoverage;
              };

              // Handle streaming tokens
              if (data.delta) {
                accumulatedContent += data.delta;
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[assistantMessageIndex] = {
                    role: "assistant",
                    content: accumulatedContent,
                  };
                  return updated;
                });
              }

              // Handle the `done` event. Coverage drives both the in-interview
              // progress display and the transition into the review phase.
              if (data.coverage) {
                setCoverage(data.coverage);
                if (data.coverage.isComplete) {
                  setPhase("review");
                }
              } else if (data.isComplete) {
                // Tolerated fallback for a server that predates the coverage
                // payload; the phase still lands on `review`, never `dedupe`.
                setPhase("review");
              }
            } catch (e) {
              console.error("Failed to parse SSE data:", e);
            }
          }
        }
      }

      // If the server sent an `event: error` payload, surface it now that the
      // stream has ended so the user sees a real error instead of an empty
      // assistant placeholder.
      if (sseError) {
        const errorMessage =
          sseError.code === "SESSION_EXPIRED"
            ? "Interview session has expired. Please start a new interview."
            : sseError.message || "The server reported an error while streaming the response.";
        setError(errorMessage);
        setMessages((prev) => prev.slice(0, -1));
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        // Request was aborted - don't show error
        return;
      }

      let errorMessage: string;
      if (err instanceof ApiError) {
        if (err.response.status === 410) {
          errorMessage = "Interview session has expired. Please start a new interview.";
        } else if (err.response.status === 503) {
          errorMessage = "FogBugz configuration is missing. Please contact your administrator.";
        } else {
          errorMessage = err.message || `Failed to send message: ${err.response.statusText}`;
        }
      } else {
        errorMessage = err instanceof Error ? err.message : "Failed to send message";
      }
      setError(errorMessage);

      // Remove the placeholder assistant message on error
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleRetry = () => {
    setError(null);
    // Retry by resending the last user message
    const lastUserMessage = messages
      .slice()
      .reverse()
      .find((m) => m.role === "user");
    if (lastUserMessage) {
      // Don't re-append the user message — it's already in the chat history
      // from the failed attempt; otherwise the user sees their message
      // duplicated after every retry.
      handleSend(lastUserMessage.content, { appendUserMessage: false });
    }
  };

  /**
   * Persist the reporter's edits before leaving the review phase.
   *
   * The server re-parses the submitted markdown and recomputes coverage — a
   * hand-edited intent is untrusted input, so the client is never the only
   * validator. Only a successful round-trip advances the wizard, which is what
   * guarantees the filed case body and the attached `intent.md` reflect the
   * edited text rather than the pre-edit version.
   */
  const handleReviewContinue = async (intentText: string) => {
    setError(null);
    try {
      // Only the document is sent. Routing was chosen and validated on the
      // routing screen before the interview began, so there is nothing here
      // that could re-route the case.
      const result = await reviewMutation.mutateAsync({ sessionId, intentText });
      setCoverage(result.data.coverage);
      setCheckedBugBar(result.data.bugBar ?? null);
      if (!result.data.coverage.isComplete) {
        // Defensive: the server disagreed with the client's coverage check.
        // Stay on review rather than filing a partial intent.
        setError("The edited intent is still incomplete. Please fill the remaining sections.");
        return;
      }
      if (bugBarBlocks(result.data.bugBar)) {
        // A bug case below the Bug bar is never filed. Stay on review, where the
        // panel names each missing rule; the reporter edits and checks again.
        return;
      }
      setPhase("dedupe");
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? err.message || "The server rejected the edited intent."
          : err instanceof Error
            ? err.message
            : "Failed to save the edited intent";
      setError(errorMessage);
    }
  };

  const handleProceedToSubmit = () => {
    setPhase("submit");
  };

  const handleSubmit = async () => {
    try {
      const result = await submitMutation.mutateAsync(sessionId);
      // Show success briefly, then navigate to the new case
      setTimeout(() => {
        navigate(`/fogbugz/tickets/${result.data.caseId}`);
      }, 1000);
    } catch (err) {
      const refusal = belowBugBarFrom(err);
      if (refusal) {
        // The server is the gate: a below-bar bug case is refused before any
        // FogBugz call. Show the same list the review step shows.
        setCheckedBugBar(refusal);
      }
      const errorMessage = err instanceof Error ? err.message : "Failed to submit case";
      setError(errorMessage);
    }
  };

  const handleScreenshotUpload = (description: string) => {
    // Automatically send the screenshot description as a message
    handleSend(`[Screenshot uploaded] ${description}`);
  };

  // Render phase-specific content
  if (phase === "review") {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Review Your Report</h2>
          <p className="text-sm text-muted-foreground">
            This is the record that will be filed. Correct anything that is wrong or incomplete —
            you are editing the document itself, not answering more questions.
          </p>
        </div>

        {intentQuery.isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            Loading...
          </div>
        ) : intentQuery.error ? (
          <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>Failed to load your report. Please try again.</span>
          </div>
        ) : (
          <IntentReview
            initialText={intentQuery.data?.data.intentText ?? ""}
            serverCoverage={intentQuery.data?.data.coverage ?? coverage}
            bugBar={bugBar}
            isSaving={reviewMutation.isPending}
            saveError={error}
            routing={intentQuery.data?.data.routing}
            onBack={() => {
              // The interview may change the slots, so an earlier check is stale.
              setCheckedBugBar(null);
              setPhase("interview");
            }}
            onContinue={(intentText) => {
              void handleReviewContinue(intentText);
            }}
          />
        )}
      </div>
    );
  }

  if (phase === "dedupe") {
    const dedupeData = dedupeQuery.data?.data;
    // `ApiError` already carries the server's `error.message`, so a 400 such as
    // "Stored FogBugz key could not be decrypted … re-save your FogBugz API key"
    // reaches the reporter verbatim instead of a generic "try again".
    const dedupeErrorMessage =
      dedupeQuery.error instanceof Error && dedupeQuery.error.message
        ? dedupeQuery.error.message
        : "Failed to check for duplicates. Please try again.";
    const dedupeUnavailable = dedupeData?.unavailable === true;
    const dedupeCount = dedupeData?.count ?? 0;

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Check for Duplicates</h2>
          {/* Exactly one state renders below: loading, error, unavailable, or
              results. The count sentence lives inside the results branch so it
              can never appear next to an error or before the search has run. */}
          {!dedupeQuery.isLoading && !dedupeQuery.isError && dedupeData && !dedupeUnavailable && (
            <p className="text-sm text-muted-foreground" data-testid="dedupe-count">
              We found {dedupeCount} similar open {dedupeCount === 1 ? "case" : "cases"}.
              {dedupeCount > 0 ? " Please review them before submitting." : ""}
            </p>
          )}
        </div>

        {dedupeQuery.isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            Loading...
          </div>
        ) : dedupeQuery.isError ? (
          <div
            className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm"
            role="alert"
            data-testid="dedupe-error"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{dedupeErrorMessage}</span>
          </div>
        ) : dedupeUnavailable ? (
          <div
            className="p-4 border border-white/[0.08] rounded-lg text-sm text-muted-foreground"
            data-testid="dedupe-unavailable"
          >
            Duplicate check unavailable — you can still submit. FogBugz could not run the
            search for similar cases, so this report has not been compared against open cases.
          </div>
        ) : (
          <div className="space-y-3">
            {dedupeData?.similarCases.slice(0, 5).map((caseItem: DedupeCase) => (
              <div
                key={caseItem.ixBug}
                className="p-4 border border-white/[0.08] rounded-lg hover:bg-white/[0.02]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0" data-testid="dedupe-case">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-mono text-primary">#{caseItem.ixBug}</span>
                      <span className="text-xs px-2 py-0.5 bg-white/[0.08] rounded">
                        {caseItem.sStatus}
                      </span>
                      {caseItem.sProject && (
                        <span className="text-xs text-muted-foreground">
                          {caseItem.sProject}
                          {caseItem.sArea ? ` › ${caseItem.sArea}` : ""}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-medium line-clamp-2">{caseItem.sTitle}</h3>
                    {caseItem.sLatestTextSummary && (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {caseItem.sLatestTextSummary}
                      </p>
                    )}
                    <DedupeCaseMeta caseItem={caseItem} />
                  </div>
                  {(() => {
                    // `caseUrl` comes from the server; gate the href on http(s)
                    // so a malformed FogBugz response cannot turn this into a
                    // `javascript:` injection vector.
                    const safeCaseHref = safeHttpUrl(caseItem.caseUrl);
                    return safeCaseHref ? (
                      <a
                        href={safeCaseHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline flex-shrink-0"
                      >
                        View in FogBugz →
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        View in FogBugz
                      </span>
                    );
                  })()}
                </div>
              </div>
            ))}

            {dedupeCount === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No similar cases found. This appears to be a new issue.
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
          <Button variant="outline" onClick={() => setPhase("review")}>
            Back to Review
          </Button>
          <Button
            onClick={handleProceedToSubmit}
            disabled={dedupeQuery.isLoading || dedupeQuery.isError}
          >
            Continue to Submit
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "submit") {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Review &amp; Submit</h2>
          <p className="text-sm text-muted-foreground">
            Submit your case to FogBugz. It is audited automatically on submit; the quality score
            is shown here once the case is created.
          </p>
        </div>

        {submitMutation.isSuccess ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-500">
              <AlertCircle className="h-4 w-4" />
              <span>
                Case #{submitMutation.data.data.caseId} created successfully! Redirecting...
              </span>
            </div>
            {submitMutation.data.data.auditScore && (
              <AuditScoreDisplay score={submitMutation.data.data.auditScore} />
            )}
          </div>
        ) : (
          <>
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-500 text-sm">
              <p className="font-medium mb-2">Quality Check</p>
              <p className="text-muted-foreground">
                Your case is scored against the FogBugz intake rubric when you submit. You do not
                need to wait for anything here.
              </p>
            </div>

            {error && (
              <div
                className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm"
                data-testid="submit-error"
                role="alert"
              >
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}

            {bugBar && bugBarBlocks(bugBar) && (
              <BugBarPanel
                bugBar={bugBar}
                hint="Go back to your report, add what is listed, and submit again."
              />
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
              {bugBarBlocks(bugBar) ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setError(null);
                    setPhase("review");
                  }}
                >
                  Back to Review
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setPhase("dedupe")}>
                  Back
                </Button>
              )}
              <Button
                onClick={handleSubmit}
                disabled={submitMutation.isPending || bugBarBlocks(bugBar)}
                title={
                  bugBarBlocks(bugBar)
                    ? "This bug case is not ready to file yet. Add what is listed above first."
                    : undefined
                }
              >
                {submitMutation.isPending ? "Submitting..." : "Submit to FogBugz"}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Interview phase
  return (
    <div
      ref={panelRef}
      className="flex flex-col h-[calc(100vh-200px)] max-h-[800px]"
      style={minPanelHeight !== null ? { minHeight: minPanelHeight } : undefined}
    >
      {/* Coverage progress — driven by the server's slot coverage, not by a
          binary "the model thinks it is done" flag. */}
      {coverage && <CoverageProgress coverage={coverage} />}

      {/* Messages area */}
      <div
        ref={messagesAreaRef}
        data-testid="interview-messages"
        className="flex-1 overflow-y-auto space-y-4 p-4 border border-white/[0.08] rounded-lg mb-4"
      >
        {messages.map((msg, i) => (
          <div
            key={i}
            data-message-row
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-lg ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-white/[0.04] border border-white/[0.08]"
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Attachments: compact uploaders, side by side on wide screens, so the message
          list keeps its height. Event log summaries (PI-SPEC-SW-1229) stay in their
          panel and are not sent into the interview chat. */}
      <div className="mb-4 grid gap-3 md:grid-cols-2 md:items-start">
        <div>
          <h3 className="text-sm font-medium mb-2">Screenshots</h3>
          <ScreenshotPaste sessionId={sessionId} onUploadSuccess={handleScreenshotUpload} />
        </div>
        <div>
          <h3 className="text-sm font-medium mb-2">Event logs (.evtx)</h3>
          <EventLogUpload sessionId={sessionId} />
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4">
          <div className="flex items-center justify-between gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleRetry}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type your response..."
          disabled={isStreaming}
          className="flex-1"
        />
        <Button onClick={() => handleSend()} disabled={!input.trim() || isStreaming}>
          {isStreaming ? (
            <span className="animate-pulse">Sending...</span>
          ) : (
            <>
              <Send className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/** "Aug 14, 2026" from an ISO timestamp; null when it does not parse. */
function formatCaseDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Assignee and dates for a possible duplicate, so the reporter can judge it
 * without opening it in FogBugz. Renders nothing when FogBugz sent none of them.
 */
function DedupeCaseMeta({ caseItem }: { caseItem: DedupeCase }) {
  const opened = formatCaseDate(caseItem.dtOpened);
  const updated = formatCaseDate(caseItem.dtLastUpdated);
  const parts = [
    caseItem.sPersonAssignedTo ? `Assigned to ${caseItem.sPersonAssignedTo}` : null,
    opened ? `Opened ${opened}` : null,
    updated && updated !== opened ? `Updated ${updated}` : null,
  ].filter((part): part is string => part !== null);

  if (parts.length === 0) return null;
  return <p className="mt-1 text-xs text-muted-foreground">{parts.join(" · ")}</p>;
}

/**
 * Remaining-slot progress for the interview phase.
 *
 * Everything here comes from the server's coverage payload: the reporter sees
 * how many of the report's sections still need an answer and which ones they
 * are, instead of a binary "complete" flag they cannot act on.
 */
function CoverageProgress({ coverage }: { coverage: InterviewCoverage }) {
  const remaining = [...coverage.requiredRemaining, ...coverage.optionalRemaining];
  const answered = Math.max(coverage.totalSlots - remaining.length, 0);
  const percent = coverage.totalSlots > 0 ? Math.round((answered / coverage.totalSlots) * 100) : 0;

  return (
    <div className="mb-4 p-3 border border-white/[0.08] rounded-lg space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">Report progress</span>
        <span className="text-muted-foreground" data-testid="coverage-count">
          {answered} of {coverage.totalSlots} answered
        </span>
      </div>

      <div
        className="h-1.5 w-full rounded-full bg-white/[0.08] overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={coverage.totalSlots}
        aria-valuenow={answered}
        aria-label="Report progress"
      >
        <div
          className={cn(
            "h-full rounded-full transition-all",
            coverage.isComplete ? "bg-green-500" : "bg-primary"
          )}
          style={{ width: `${String(percent)}%` }}
        />
      </div>

      {remaining.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="coverage-remaining">
          Still to cover: {remaining.map((id) => slotLabel(id)).join(", ")}
        </p>
      )}
    </div>
  );
}

function AuditScoreDisplay({ score }: { score: AuditScore }) {
  const tierColors = {
    pass: "text-green-500 bg-green-500/10 border-green-500/20",
    advisory: "text-yellow-500 bg-yellow-500/10 border-yellow-500/20",
    "critical-fail": "text-red-500 bg-red-500/10 border-red-500/20",
  };

  // A kind with no rubric is not a zero-scoring ticket, so it never borrows the
  // tier's colour or prints a number. Saying "0" next to "pass" invites the
  // reporter to read a clean submission as a failing one.
  const notApplicable = score.maxTotal === 0;

  return (
    <div className="space-y-3">
      <div
        className={`p-4 border rounded-lg ${
          notApplicable ? "text-muted-foreground bg-white/[0.04] border-white/[0.08]" : tierColors[score.tier]
        }`}
        data-testid="audit-score"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium">Audit Score</span>
          <span className="text-2xl font-bold" data-testid="audit-score-value">
            {notApplicable ? "Not scored" : `${String(score.overall)}/${String(score.maxTotal)}`}
          </span>
        </div>
        <div className="text-xs uppercase tracking-wider">
          {notApplicable ? `no audit rubric applies to a ${score.kind}` : score.tier}
        </div>
      </div>

      {score.issues.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Issues Found:</h4>
          {score.issues.map((issue, i) => (
            <div key={i} className="p-3 bg-white/[0.04] border border-white/[0.08] rounded text-sm">
              <div className="flex items-start gap-2">
                <span
                  className={`px-2 py-0.5 text-xs rounded ${
                    issue.severity === "high" || issue.severity === "critical"
                      ? "bg-red-500/20 text-red-500"
                      : "bg-yellow-500/20 text-yellow-500"
                  }`}
                >
                  {issue.severity}
                </span>
                <span className="text-muted-foreground">{issue.message}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
